"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

import { GhostErrorLayout } from "@/components/error/GhostErrorLayout";

function isNextInternalError(error: Error & { digest?: string }): boolean {
  const digest = error.digest;
  if (!digest) return false;
  return (
    digest.startsWith("NEXT_HTTP_ERROR_FALLBACK") ||
    digest.startsWith("NEXT_REDIRECT")
  );
}

/**
 * Last-resort error page. Replaces the ENTIRE DOM (including <html>, <body>,
 * and all layouts). Uses GhostErrorLayout (inline styles only) to render a
 * full-viewport ghost of the app with the error card centered on top.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (!isNextInternalError(error)) {
      Sentry.captureException(error);
    }
    console.error(`Global error: ${error.message}`);
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0 }}>
        <GhostErrorLayout>
          <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#6b7280",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            An unexpected error occurred. You can try again or return to the
            home page.
          </p>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              onClick={reset}
              style={{
                padding: "0.5rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#fff",
                backgroundColor: "#18181b",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error replaces the entire DOM; Next.js <Link> is unavailable */}
            <a
              href="/"
              style={{
                padding: "0.5rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
                backgroundColor: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: "0.375rem",
                textDecoration: "none",
                cursor: "pointer",
                display: "inline-block",
              }}
            >
              Go to home
            </a>
          </div>
        </GhostErrorLayout>
      </body>
    </html>
  );
}
